import { Gamepad2, Landmark, HeartPulse, GraduationCap, ShoppingCart, Radio, FlaskConical, ShieldCheck, Lock, KeyRound, Webhook, FileLock2 } from "lucide-react";

const INDUSTRIES = [
  { icon: Gamepad2, t: "Game Studios", d: "Turn-based, real-time, multiplayer and live-ops backends." },
  { icon: Landmark, t: "Fintech", d: "Low-latency, auditable transaction and ledger systems." },
  { icon: HeartPulse, t: "HealthTech", d: "Secure, compliant data pipelines and services." },
  { icon: GraduationCap, t: "EdTech", d: "Interactive, gamified learning platforms at scale." },
  { icon: ShoppingCart, t: "E-commerce", d: "High-throughput catalogs, carts and recommendation APIs." },
  { icon: Radio, t: "IoT & Telecom", d: "Real-time device streams and event-driven microservices." },
  { icon: FlaskConical, t: "Simulation & Research", d: "Digital-twin modeling with Panda3D and Matlab." },
];

const SECURITY = [
  { icon: Lock, t: "Secure authentication", d: "Google OAuth with encrypted, expiring server-side sessions — no passwords stored." },
  { icon: KeyRound, t: "Least-privilege API keys", d: "Scoped, revocable keys per connector with per-call usage tracking." },
  { icon: Webhook, t: "Verified webhooks", d: "Payment callbacks validated with signatures and re-checked server-side." },
  { icon: FileLock2, t: "Hardened transport", d: "HTTPS everywhere, strict security headers and secret management via environment vaults." },
];

export const IndustriesSecurity = () => (
  <section id="industries" className="py-24 px-4 sm:px-6 lg:px-8 bg-slate-950/40 border-y border-slate-800/60">
    <div className="max-w-7xl mx-auto">
      <div className="max-w-2xl mb-14">
        <span className="text-xs font-mono uppercase tracking-widest text-emerald-400">Industries &amp; Opportunity</span>
        <h2 className="mt-3 font-display text-3xl lg:text-4xl font-bold tracking-tight text-slate-100">
          Built for games — ready for any high-performance backend
        </h2>
        <p className="mt-4 text-slate-400 leading-relaxed">
          The same digital-twin engine that powers game systems translates business requirements into robust backends across industries — opening new opportunities wherever speed, scale and reliability matter.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        {INDUSTRIES.map((n) => (
          <div key={n.t} data-testid={`industry-${n.t}`}
            className="glass rounded-xl border border-slate-800 p-5 hover:border-emerald-500/40 transition-colors">
            <n.icon className="w-6 h-6 text-emerald-400 mb-3" />
            <h3 className="text-slate-100 font-medium text-sm">{n.t}</h3>
            <p className="mt-1.5 text-xs text-slate-500 leading-relaxed">{n.d}</p>
          </div>
        ))}
      </div>

      <div id="security" className="mt-16">
        <div className="flex items-center gap-2 mb-5">
          <ShieldCheck className="w-5 h-5 text-emerald-400" />
          <h3 className="font-display text-2xl font-semibold text-slate-100">Security, reinforced</h3>
        </div>
        <p className="max-w-2xl text-slate-400 leading-relaxed text-sm mb-8">
          Security is built in, not bolted on. Every layer — from auth to APIs to payments — is designed to protect your data and your players.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {SECURITY.map((s) => (
            <div key={s.t} data-testid={`security-${s.t}`}
              className="glass rounded-xl border border-slate-800 p-6 hover:border-emerald-500/50 transition-colors">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-4">
                <s.icon className="w-5 h-5 text-emerald-400" />
              </div>
              <h4 className="font-display font-semibold text-slate-100 text-sm">{s.t}</h4>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);
